import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import { AlertTriangle, Check, ExternalLink, Pencil, Trash2, X } from 'lucide-react';
import type { ParseKeys } from 'i18next';
import { useId, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge, Button, Checkbox, Input, Textarea, useToast } from '@/components/ui';
import { useCanEdit } from '@/lib/auth';
import { cn, formatCurrency, formatDateTime } from '@/lib/utils';
import { WorkflowCardPreview } from '@/features/workflows/workflow-card-preview';
import { assistantApi, type AssistantAction } from './assistant-api';

/* ---------------------------------------------------------------------------
   כרטיס פעולה מוצעת. שום דבר לא נכתב עד "אישור": השרת מאמת שוב, בודק את
   התפקיד של מי שמאשר, ומבצע דרך השירות הקיים. צופה רואה את הכרטיס בלי
   כפתורי אישור/עריכה (useCanEdit + canConfirm מהשרת).
   --------------------------------------------------------------------------- */

type Rec = Record<string, unknown>;
const str = (v: unknown): string =>
  typeof v === 'string' ? v : typeof v === 'number' ? String(v) : '';
const arr = (v: unknown): Rec[] => (Array.isArray(v) ? (v as Rec[]) : []);
const obj = (v: unknown): Rec =>
  v && typeof v === 'object' && !Array.isArray(v) ? (v as Rec) : {};

const STATUS_TONE = {
  PENDING: 'warning',
  CONFIRMED: 'success',
  CANCELLED: 'neutral',
  FAILED: 'danger',
} as const;
const STATUS_KEY = {
  PENDING: 'card.pending',
  CONFIRMED: 'card.confirmed',
  CANCELLED: 'card.cancelled',
  FAILED: 'card.failed',
} as const satisfies Record<AssistantAction['status'], ParseKeys<'assistant'>>;

/** פעולות שרק אדמין (בעלים) מאשר — לטקסט ההסבר כשאין כפתור. */
const OWNER_ONLY = new Set(['team_member', 'business_details', 'finish_setup', 'workflow']);

interface Invite {
  temporaryPassword: string;
  waUrl: string | null;
  name: string;
}

export function ActionCard({
  action,
  onChange,
}: {
  action: AssistantAction;
  onChange: (a: AssistantAction) => void;
}) {
  const { t } = useTranslation('assistant');
  const toast = useToast();
  const qc = useQueryClient();
  const router = useRouter();
  const canEdit = useCanEdit();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Rec>(action.payload);
  const [invite, setInvite] = useState<Invite | null>(null);
  const titleId = useId();

  const pending = action.status === 'PENDING';
  const mayApprove = pending && canEdit && action.canConfirm;

  const confirm = useMutation({
    mutationFn: () => assistantApi.confirm(action.id),
    onSuccess: ({ action: next, invite: inv }) => {
      onChange(next);
      if (next.status === 'CONFIRMED') {
        toast.success(t('toast.confirmed'));
        // מה שנוצר מופיע במסכים בלי רענון.
        void qc.invalidateQueries({ predicate: (q) => q.queryKey[0] !== 'assistant' });
      } else toast.error(t('toast.failed'), next.error ?? undefined);
      if (inv)
        setInvite({
          temporaryPassword: inv.temporaryPassword,
          waUrl: inv.waUrl,
          name: str(action.payload['name']),
        });
    },
    onError: (err: Error) => toast.error(t('toast.failed'), err.message),
  });
  const cancel = useMutation({
    mutationFn: () => assistantApi.cancel(action.id),
    onSuccess: (next) => onChange(next),
    onError: (err: Error) => toast.error(t('toast.failed'), err.message),
  });
  const save = useMutation({
    mutationFn: () => assistantApi.edit(action.id, draft),
    onSuccess: (next) => {
      onChange(next);
      setEditing(false);
    },
    onError: (err: Error) => toast.error(t('toast.failed'), err.message),
  });

  const busy = confirm.isPending || cancel.isPending || save.isPending;
  const href = action.result?.href;

  return (
    <section
      aria-labelledby={titleId}
      className={cn(
        'mt-2 rounded-(--radius-lg) border bg-surface-raised p-3 text-sm shadow-xs',
        pending ? 'border-accent-border' : 'border-border',
      )}
    >
      <header className="mb-2 flex items-center justify-between gap-2">
        <h3 id={titleId} className="font-semibold text-fg">
          {t(`card.kind.${action.kind}`)}
        </h3>
        <Badge tone={STATUS_TONE[action.status]}>{t(STATUS_KEY[action.status])}</Badge>
      </header>

      {editing ? (
        <Editor kind={action.kind} draft={draft} setDraft={setDraft} />
      ) : (
        <Preview action={action} />
      )}

      {action.status === 'FAILED' && action.error ? (
        <p role="alert" className="mt-2 text-xs text-danger">
          {action.error}
        </p>
      ) : null}

      {invite ? <InviteBox invite={invite} /> : null}

      {action.status === 'CONFIRMED' && href ? (
        <Button variant="link" size="sm" className="mt-2" onClick={() => router.history.push(href)}>
          <ExternalLink aria-hidden />
          {action.result?.label
            ? t('card.approved', { label: action.result.label })
            : t('card.open')}
        </Button>
      ) : null}

      {pending && !mayApprove ? (
        <p className="mt-2 text-xs text-fg-muted">
          {canEdit && OWNER_ONLY.has(action.kind) ? t('card.ownerOnly') : t('card.viewOnly')}
        </p>
      ) : null}

      {pending ? (
        <div className="mt-3 flex flex-wrap gap-2">
          {editing ? (
            <>
              <Button
                size="sm"
                onClick={() => save.mutate()}
                loading={save.isPending}
                disabled={busy}
              >
                <Check aria-hidden />
                {t('card.save')}
              </Button>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  setDraft(action.payload);
                  setEditing(false);
                }}
                disabled={busy}
              >
                {t('card.discard')}
              </Button>
            </>
          ) : (
            <>
              {mayApprove ? (
                <Button
                  size="sm"
                  onClick={() => confirm.mutate()}
                  loading={confirm.isPending}
                  disabled={busy}
                >
                  <Check aria-hidden />
                  {t('card.approve')}
                </Button>
              ) : null}
              {mayApprove && action.kind !== 'finish_setup' ? (
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => {
                    setDraft(action.payload);
                    setEditing(true);
                  }}
                  disabled={busy}
                >
                  <Pencil aria-hidden />
                  {t('card.edit')}
                </Button>
              ) : null}
              <Button
                size="sm"
                variant="ghost"
                onClick={() => cancel.mutate()}
                loading={cancel.isPending}
                disabled={busy}
              >
                <X aria-hidden />
                {t('card.cancel')}
              </Button>
            </>
          )}
        </div>
      ) : null}
    </section>
  );
}

// ---------------------------------------------------------------------------
// תצוגה
// ---------------------------------------------------------------------------

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex gap-2">
      <dt className="min-w-24 text-fg-muted">{label}</dt>
      <dd className="min-w-0 flex-1 break-words text-fg">{children}</dd>
    </div>
  );
}

function Preview({ action }: { action: AssistantAction }) {
  const { t } = useTranslation('assistant');
  const p = action.preview;
  switch (action.kind) {
    case 'quote':
      return <QuotePreview preview={p} />;
    case 'customer': {
      const dups = arr(p['possibleDuplicates']);
      return (
        <dl className="space-y-1">
          <Row label={t('card.fields.name')}>{str(p['name'])}</Row>
          {p['phone'] ? <Row label={t('card.fields.phone')}>{str(p['phone'])}</Row> : null}
          {p['email'] ? <Row label={t('card.fields.email')}>{str(p['email'])}</Row> : null}
          {p['address'] ? <Row label={t('card.fields.address')}>{str(p['address'])}</Row> : null}
          {dups.length ? (
            <p className="flex items-center gap-1 text-xs text-warning">
              <AlertTriangle className="size-3.5" aria-hidden />
              {t('card.duplicates', { names: dups.map((d) => str(d['name'])).join(', ') })}
            </p>
          ) : null}
        </dl>
      );
    }
    case 'task':
      return (
        <dl className="space-y-1">
          <Row label={t('card.fields.title')}>{str(p['title'])}</Row>
          <Row label={t('card.fields.jobType')}>{str(p['jobTypeName'])}</Row>
          {p['customerName'] ? (
            <Row label={t('card.quote.customer')}>{str(p['customerName'])}</Row>
          ) : null}
          {p['scheduledStart'] ? (
            <Row label={t('card.fields.when')}>{formatDateTime(str(p['scheduledStart']))}</Row>
          ) : null}
          {p['assigneeName'] ? (
            <Row label={t('card.fields.assignee')}>{str(p['assigneeName'])}</Row>
          ) : null}
          {p['description'] ? (
            <Row label={t('card.fields.description')}>{str(p['description'])}</Row>
          ) : null}
        </dl>
      );
    case 'price_items':
      return (
        <ul className="divide-y divide-border">
          {arr(p['items']).map((i) => (
            <li key={str(i['code'])} className="flex items-center justify-between gap-2 py-1">
              <span className="min-w-0">
                <span className="font-mono text-xs text-fg-muted [direction:ltr]">
                  {str(i['code'])}
                </span>{' '}
                {str(i['description'])}
                {i['exists'] ? (
                  <Badge tone="warning" className="ms-2">
                    {t('card.exists')}
                  </Badge>
                ) : null}
              </span>
              <span className="shrink-0 tabular-nums">{formatCurrency(str(i['price']))}</span>
            </li>
          ))}
        </ul>
      );
    case 'job_type':
      return (
        <dl className="space-y-1">
          <Row label={t('card.fields.name')}>{str(p['name'])}</Row>
          {p['requiredSkill'] ? (
            <Row label={t('card.fields.requiredSkill')}>{str(p['requiredSkill'])}</Row>
          ) : null}
          {p['durationMinutes'] ? (
            <Row label={t('card.fields.duration')}>{str(p['durationMinutes'])}</Row>
          ) : null}
          {arr(p['checklist']).length ? (
            <Row label={t('card.fields.checklist')}>
              <ul className="list-disc ps-4">
                {arr(p['checklist']).map((c, i) => (
                  <li key={i}>{str(c['label'])}</li>
                ))}
              </ul>
            </Row>
          ) : null}
        </dl>
      );
    case 'team_member':
      return (
        <dl className="space-y-1">
          <Row label={t('card.fields.name')}>{str(p['name'])}</Row>
          <Row label={t('card.fields.role')}>{roleLabel(t, str(p['role']))}</Row>
          {p['email'] ? <Row label={t('card.fields.email')}>{str(p['email'])}</Row> : null}
          {p['phone'] ? <Row label={t('card.fields.phone')}>{str(p['phone'])}</Row> : null}
        </dl>
      );
    case 'business_details':
      return (
        <dl className="space-y-1">
          {arr(p['changes']).map((c) => (
            <Row key={str(c['field'])} label={fieldLabel(t, str(c['field']))}>
              {str(c['to'])}
              {str(c['from']) ? (
                <span className="ms-2 text-xs text-fg-subtle">
                  {t('card.from', { value: str(c['from']) })}
                </span>
              ) : null}
            </Row>
          ))}
        </dl>
      );
    case 'finish_setup':
      return (
        <div className="space-y-1">
          {p['summary'] ? <p>{str(p['summary'])}</p> : null}
          <ul className="text-xs text-fg-muted">
            <li>
              {p['businessDetails'] ? '✓ ' : '• '}
              {t('card.setup.businessDetails')}
            </li>
            <li>{t('card.setup.team', { count: Number(p['teamMembers'] ?? 0) })}</li>
            <li>{t('card.setup.priceItems', { count: Number(p['priceItems'] ?? 0) })}</li>
            <li>{t('card.setup.jobTypes', { count: Number(p['jobTypes'] ?? 0) })}</li>
            <li>{t('card.setup.customers', { count: Number(p['customers'] ?? 0) })}</li>
          </ul>
        </div>
      );
    case 'workflow':
      return <WorkflowCardPreview preview={p} />;
  }
}

function QuotePreview({ preview }: { preview: Rec }) {
  const { t } = useTranslation('assistant');
  const customer = obj(preview['customer']);
  const totals = obj(preview['totals']);
  // הנחה רשמית על כל ההצעה (שורה נפרדת, לפני מע"מ). כרטיס ישן — discountPercent.
  const discount = obj(preview['discount']);
  const legacyPct = Number(preview['discountPercent'] ?? 0);
  const hasDiscount = Number(totals['discount'] ?? 0) > 0;
  const pct =
    discount['type'] === 'PERCENT'
      ? Number(discount['value'])
      : !discount['type'] && legacyPct > 0
        ? legacyPct
        : null;
  const discountLabel = str(discount['label']) || t('card.quote.discountRow');
  return (
    <div className="space-y-2">
      <p>
        <span className="text-fg-muted">{t('card.quote.customer')}: </span>
        <span className="font-medium">{str(customer['name'])}</span>
        {customer['isNew'] ? (
          <Badge tone="info" className="ms-2">
            {t('card.quote.newCustomer')}
          </Badge>
        ) : null}
      </p>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="text-fg-muted">
            <tr>
              <th scope="col" className="py-1 text-start font-medium">
                {t('card.quote.item')}
              </th>
              <th scope="col" className="py-1 text-end font-medium">
                {t('card.quote.qty')}
              </th>
              <th scope="col" className="py-1 text-end font-medium">
                {t('card.quote.unitPrice')}
              </th>
              <th scope="col" className="py-1 text-end font-medium">
                {t('card.quote.amount')}
              </th>
            </tr>
          </thead>
          <tbody>
            {arr(preview['lines']).map((l, i) => (
              <tr key={i} className="border-t border-border align-top">
                <td className="py-1">
                  {str(l['description'])}
                  <div className="mt-0.5 flex flex-wrap gap-1">
                    {l['priceCode'] ? (
                      <Badge tone="neutral">{t('card.quote.fromList')}</Badge>
                    ) : null}
                    {l['saveToPriceList'] ? (
                      <Badge tone="accent">{t('card.quote.saveToList')}</Badge>
                    ) : null}
                    {l['estimated'] ? (
                      <Badge tone="warning">
                        <AlertTriangle className="size-3" aria-hidden />
                        {t('card.quote.estimate')}
                      </Badge>
                    ) : null}
                  </div>
                </td>
                <td className="py-1 text-end tabular-nums">{str(l['qty'])}</td>
                <td className="py-1 text-end tabular-nums">
                  {formatCurrency(str(l['unitPrice']))}
                </td>
                <td className="py-1 text-end tabular-nums">{formatCurrency(str(l['amount']))}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <dl className="space-y-0.5 border-t border-border pt-2 text-xs">
        {hasDiscount ? (
          <>
            <Total label={t('card.quote.subtotal')} value={str(totals['subtotal'])} />
            <Total
              label={
                pct !== null
                  ? t('card.quote.discount', { label: discountLabel, pct })
                  : discountLabel
              }
              value={`-${str(totals['discount'])}`}
            />
          </>
        ) : null}
        <Total label={t('card.quote.net')} value={str(totals['net'])} />
        {totals['vatMode'] === 'added' ? (
          <Total
            label={t('card.quote.vat', { rate: Number(totals['vatRate'] ?? 0) })}
            value={str(totals['vat'])}
          />
        ) : null}
        <Total label={t('card.quote.total')} value={str(totals['total'])} strong />
      </dl>
      {hasDiscount ? (
        <p className="text-2xs text-fg-subtle">{t('card.quote.discountNote')}</p>
      ) : null}
      {preview['notes'] ? (
        <p className="text-xs">
          <span className="text-fg-muted">{t('card.quote.notes')}: </span>
          {str(preview['notes'])}
        </p>
      ) : null}
      <p className="text-2xs text-fg-subtle">{t('card.quote.draftNote')}</p>
    </div>
  );
}

function Total({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className={cn('flex justify-between gap-2', strong && 'text-sm font-semibold')}>
      <dt className={strong ? 'text-fg' : 'text-fg-muted'}>{label}</dt>
      <dd className="tabular-nums">
        {value.startsWith('-') ? `-${formatCurrency(value.slice(1))}` : formatCurrency(value)}
      </dd>
    </div>
  );
}

function InviteBox({ invite }: { invite: Invite }) {
  const { t } = useTranslation('assistant');
  return (
    <div className="mt-2 rounded-(--radius-md) border border-info-border bg-info-subtle p-2 text-xs">
      <p className="font-medium">{t('card.invite.title', { name: invite.name })}</p>
      <p>
        {t('card.invite.password')}:{' '}
        <span className="font-mono [direction:ltr]">{invite.temporaryPassword}</span>
      </p>
      <p className="text-fg-muted">{t('card.invite.once')}</p>
      {invite.waUrl ? (
        <a
          href={invite.waUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="font-medium text-accent hover:underline"
        >
          {t('card.invite.whatsapp')}
        </a>
      ) : null}
    </div>
  );
}

type T = ReturnType<typeof useTranslation<'assistant'>>['t'];

const FIELD_KEYS = {
  name: 'card.fields.name',
  phone: 'card.fields.phone',
  email: 'card.fields.email',
  address: 'card.fields.address',
  title: 'card.fields.title',
  description: 'card.fields.description',
  legalName: 'card.fields.legalName',
  taxId: 'card.fields.taxId',
  trade: 'card.fields.trade',
  locale: 'card.fields.locale',
  requiredSkill: 'card.fields.requiredSkill',
  durationMinutes: 'card.fields.duration',
  scheduledStart: 'card.fields.when',
  notes: 'card.quote.notes',
} as const satisfies Record<string, ParseKeys<'assistant'>>;

function fieldLabel(t: T, field: string): string {
  return field in FIELD_KEYS ? t(FIELD_KEYS[field as keyof typeof FIELD_KEYS]) : field;
}

const ROLE_KEYS = {
  OWNER: 'card.roles.OWNER',
  MANAGER: 'card.roles.MANAGER',
  VIEWER: 'card.roles.VIEWER',
  FIELD: 'card.roles.FIELD',
} as const satisfies Record<string, ParseKeys<'assistant'>>;

function roleLabel(t: T, role: string): string {
  return role in ROLE_KEYS ? t(ROLE_KEYS[role as keyof typeof ROLE_KEYS]) : role;
}

// ---------------------------------------------------------------------------
// עריכה — שדות פשוטים במקום, מאומתים שוב בשרת (PATCH) לפני שהכרטיס מתעדכן.
// ---------------------------------------------------------------------------

const FLAT_FIELDS: Partial<Record<AssistantAction['kind'], Array<keyof typeof FIELD_KEYS>>> = {
  workflow: ['name'],
  customer: ['name', 'phone', 'email', 'address'],
  task: ['title', 'description'],
  job_type: ['name', 'requiredSkill'],
  team_member: ['name', 'email', 'phone'],
  business_details: ['legalName', 'taxId', 'phone', 'email', 'address'],
};

function Editor({
  kind,
  draft,
  setDraft,
}: {
  kind: AssistantAction['kind'];
  draft: Rec;
  setDraft: (d: Rec) => void;
}) {
  const { t } = useTranslation('assistant');
  const base = useId();
  if (kind === 'quote') return <QuoteEditor draft={draft} setDraft={setDraft} />;
  if (kind === 'price_items') {
    const items = arr(draft['items']);
    const setItem = (i: number, patch: Rec) =>
      setDraft({ ...draft, items: items.map((it, j) => (j === i ? { ...it, ...patch } : it)) });
    return (
      <ul className="space-y-2">
        {items.map((it, i) => (
          <li key={i} className="grid grid-cols-[1fr_2fr_1fr] gap-2">
            <Input
              aria-label={t('card.fields.code')}
              value={str(it['code'])}
              onChange={(e) => setItem(i, { code: e.target.value })}
              className="h-9"
            />
            <Input
              aria-label={t('card.fields.description')}
              value={str(it['description'])}
              onChange={(e) => setItem(i, { description: e.target.value })}
              className="h-9"
            />
            <Input
              aria-label={t('card.fields.price')}
              inputMode="decimal"
              value={str(it['price'])}
              onChange={(e) => setItem(i, { price: e.target.value })}
              className="h-9"
            />
          </li>
        ))}
      </ul>
    );
  }
  const fields = FLAT_FIELDS[kind] ?? [];
  return (
    <div className="space-y-2">
      {fields.map((f) => (
        <label key={f} htmlFor={`${base}-${f}`} className="block text-xs text-fg-muted">
          {t(FIELD_KEYS[f])}
          {f === 'description' ? (
            <Textarea
              id={`${base}-${f}`}
              value={str(draft[f])}
              onChange={(e) => setDraft({ ...draft, [f]: e.target.value })}
              className="mt-1"
            />
          ) : (
            <Input
              id={`${base}-${f}`}
              type={f === 'email' ? 'email' : f === 'phone' ? 'tel' : 'text'}
              value={str(draft[f])}
              onChange={(e) => setDraft({ ...draft, [f]: e.target.value || undefined })}
              className="mt-1 h-9"
            />
          )}
        </label>
      ))}
    </div>
  );
}

/**
 * ההנחה בכרטיס: אחוז / סכום, ערך ותווית. ריק = בלי הנחה. כרטיס ישן
 * (discountPercent) נפתח כאחוז, ונשמר בצורה החדשה.
 */
function QuoteDiscountEditor({ draft, setDraft }: { draft: Rec; setDraft: (d: Rec) => void }) {
  const { t } = useTranslation('assistant');
  const base = useId();
  const current = obj(draft['discount']);
  const legacy = draft['discountPercent'];
  // הסוג נשמר מקומית גם כשעוד אין ערך (בחירה ב"₪" לפני שהקלידו סכום).
  const [chosen, setChosen] = useState<'PERCENT' | 'AMOUNT'>(
    current['type'] === 'AMOUNT' ? 'AMOUNT' : 'PERCENT',
  );
  const type =
    current['type'] === 'AMOUNT' || current['type'] === 'PERCENT' ? current['type'] : chosen;
  const value = current['type'] ? str(current['value']) : legacy ? str(legacy) : '';
  const label = str(current['label']);
  const update = (patch: { type?: string; value?: string; label?: string }) => {
    const next = { type, value, label, ...patch };
    const { discountPercent: _legacy, discount: _old, ...rest } = draft;
    setDraft(
      next.value.trim() === ''
        ? rest
        : {
            ...rest,
            discount: {
              type: next.type,
              value: next.value.trim(),
              ...(next.label.trim() ? { label: next.label.trim() } : {}),
            },
          },
    );
  };
  return (
    <fieldset className="space-y-1">
      <legend className="text-xs text-fg-muted">{t('card.quote.discountLegend')}</legend>
      <div className="flex flex-wrap items-center gap-2">
        <div
          role="group"
          aria-label={t('card.quote.discountType')}
          className="flex rounded-(--radius-md) border border-border p-0.5"
        >
          {(['PERCENT', 'AMOUNT'] as const).map((k) => (
            <Button
              key={k}
              type="button"
              size="sm"
              variant={type === k ? 'primary' : 'ghost'}
              aria-pressed={type === k}
              onClick={() => {
                setChosen(k);
                update({ type: k });
              }}
            >
              {k === 'PERCENT'
                ? t('card.quote.discountPercentType')
                : t('card.quote.discountAmountType')}
            </Button>
          ))}
        </div>
        <Input
          id={`${base}-discount`}
          aria-label={t('card.quote.discountValue')}
          inputMode="decimal"
          dir="ltr"
          value={value}
          onChange={(e) => update({ value: e.target.value })}
          className="h-9 w-24"
        />
        <Input
          id={`${base}-discount-label`}
          aria-label={t('card.quote.discountLabel')}
          placeholder={t('card.quote.discountLabel')}
          maxLength={80}
          value={label}
          onChange={(e) => update({ label: e.target.value })}
          className="h-9 min-w-0 flex-1"
        />
      </div>
    </fieldset>
  );
}

function QuoteEditor({ draft, setDraft }: { draft: Rec; setDraft: (d: Rec) => void }) {
  const { t } = useTranslation('assistant');
  const base = useId();
  const lines = arr(draft['lines']);
  const setLine = (i: number, patch: Rec) =>
    setDraft({ ...draft, lines: lines.map((l, j) => (j === i ? { ...l, ...patch } : l)) });
  return (
    <div className="space-y-2">
      <ul className="space-y-2">
        {lines.map((l, i) => {
          const free = !l['priceCode'];
          return (
            <li key={i} className="rounded-(--radius-md) border border-border p-2">
              <div className="flex items-start gap-2">
                <div className="min-w-0 flex-1 space-y-1">
                  {free ? (
                    <Input
                      aria-label={t('card.quote.item')}
                      value={str(l['description'])}
                      onChange={(e) => setLine(i, { description: e.target.value })}
                      className="h-9"
                    />
                  ) : (
                    <p className="font-mono text-xs [direction:ltr]">{str(l['priceCode'])}</p>
                  )}
                  <div className="flex gap-2">
                    <Input
                      aria-label={t('card.quote.qty')}
                      type="number"
                      min={1}
                      value={str(l['qty'])}
                      onChange={(e) => setLine(i, { qty: Number(e.target.value) })}
                      className="h-9 w-20"
                    />
                    {free ? (
                      <Input
                        aria-label={t('card.quote.unitPrice')}
                        inputMode="decimal"
                        value={str(l['unitPrice'])}
                        onChange={(e) =>
                          setLine(i, { unitPrice: e.target.value, priceSource: 'user' })
                        }
                        className="h-9 w-28"
                      />
                    ) : null}
                  </div>
                  {free ? (
                    <label className="flex items-center gap-2 text-xs">
                      <Checkbox
                        checked={l['saveToPriceList'] !== false}
                        onCheckedChange={(v) => setLine(i, { saveToPriceList: v === true })}
                      />
                      {t('card.quote.saveToList')}
                    </label>
                  ) : null}
                </div>
                <Button
                  size="icon"
                  variant="ghost"
                  className="size-9"
                  aria-label={t('card.quote.removeLine')}
                  disabled={lines.length <= 1}
                  onClick={() => setDraft({ ...draft, lines: lines.filter((_, j) => j !== i) })}
                >
                  <Trash2 aria-hidden />
                </Button>
              </div>
            </li>
          );
        })}
      </ul>
      <QuoteDiscountEditor draft={draft} setDraft={setDraft} />
      <label htmlFor={`${base}-notes`} className="block text-xs text-fg-muted">
        {t('card.quote.notes')}
        <Textarea
          id={`${base}-notes`}
          value={str(draft['notes'])}
          onChange={(e) => setDraft({ ...draft, notes: e.target.value || undefined })}
          className="mt-1"
        />
      </label>
    </div>
  );
}
